import type { Dictionary } from '@empathyco/x-utils'
import type { Store } from 'vuex'
import type { FeatureNode, FeaturesTree } from '../models'
import type {
  FeatureConfig,
  FeatureConfigProvider,
  FeatureProviderOptions,
  TaggingConfig,
} from './interfaces/feature-config.provider'
// @ts-strict-ignore
import { DomainEnum } from '@empathybroker/tsclient-play/dist/enums/domain.enum'
import { EngagementCategoryEnum } from '@empathybroker/tsclient-stats/dist/enums/engagement/engagement-category.enum'
import { NavigationModeEnum } from '@empathybroker/tsclient-stats/dist/enums/navigation-mode.enum'
import { interfaces } from 'inversify'
import { TYPES } from '../config/inversify.types'
import { ColorEnum, EngagementToolEnum, FeatureIdEnum } from '../enums'
import { APP_CONSTS } from '../store/app/app.consts'
import { MESSAGES_CONSTS } from '../store/messages/messages.consts'
import { FeaturesTreeUtils } from '../utils/features-tree.utils'
import Context = interfaces.Context

export function getFeatureConfigProvider(context: Context): FeatureConfigProvider {
  const store = context.container.get<Store<any>>(TYPES.Store)
  const messages: Dictionary =
    store.getters[`${MESSAGES_CONSTS.moduleName}/${MESSAGES_CONSTS.getters.messages}`]
  const featureTree: FeaturesTree =
    store.getters[`${APP_CONSTS.moduleName}/${APP_CONSTS.getters.featuresTree}`]

  return (
    featureId: FeatureIdEnum,
    options: FeatureProviderOptions = { domain: DomainEnum.SEARCH },
  ): FeatureConfig => ({
    color: getFeatureColor(featureId, featureTree[options.domain]),
    name: getFeatureName(featureId, options.domain, messages),
    taggingConfig: getTaggingConfig(featureId, options.domain),
    icon: getFeatureIcon(featureId, options),
    new: getFeatureNew(featureId),
  })
}

function getFeatureName(
  featureId: FeatureIdEnum,
  domain: DomainEnum,
  messages: Dictionary,
): string {
  const featuresMessages = messages.app.features
  const featureMessage =
    featuresMessages[featureId] instanceof Object
      ? featuresMessages[featureId][domain]
      : featuresMessages[featureId]
  return featureMessage ?? featureId
}

export function getFeatureColor(
  featureId: FeatureIdEnum | string,
  featuresNodes: Array<FeatureNode>,
): ColorEnum {
  const { parentId } = FeaturesTreeUtils.getFeatureNode(featureId, featuresNodes) ?? {}

  const colors = {
    [FeatureIdEnum.INSIGHTS]: ColorEnum.BLUE_25,
    [FeatureIdEnum.PLAY]: ColorEnum.YELLOW_25,
    [FeatureIdEnum.ROOT_QUERY]: ColorEnum.PURPLE_25,
    [FeatureIdEnum.EXPLAIN]: ColorEnum.ORANGE_25,
    [FeatureIdEnum.AUDIT]: ColorEnum.GREEN_25,
    [FeatureIdEnum.INSTANCE_MANAGEMENT]: ColorEnum.NEUTRAL_25,
  }

  return colors[featureId] ?? colors[parentId] ?? ColorEnum.PRIMARY
}

function getFeatureNew(featureId: FeatureIdEnum): boolean {
  return (
    featureId === FeatureIdEnum.SEMANTICS ||
    featureId === FeatureIdEnum.FACETS ||
    featureId === FeatureIdEnum.RELATED_PROMPTS ||
    featureId === FeatureIdEnum.RELATED_PROMPTS_ANALYTICS ||
    featureId === FeatureIdEnum.CONVERSATIONAL
  )
}

function getFeatureIcon(
  featureId: FeatureIdEnum,
  options: FeatureProviderOptions,
): string | undefined {
  const icons = {
    [FeatureIdEnum.INSIGHTS]: 'analitics',
    [FeatureIdEnum.PLAY]: 'config',
    [FeatureIdEnum.ROOT_QUERY]: 'searchdomain',
    [FeatureIdEnum.HIGHLIGHTS]: 'highlights',
    [FeatureIdEnum.EXPLAIN]: 'show',
    [FeatureIdEnum.PROMOTION]: 'promotions',
    [FeatureIdEnum.CONFIGURATION]:
      options.domain === DomainEnum.SEARCH ? 'queryranking' : 'discoveryconfiguration',
    [FeatureIdEnum.SYNONYM]: 'synonymize',
    [FeatureIdEnum.RELATED_TAGS]: 'relatedtags',
    [FeatureIdEnum.REDIRECTION]: 'redirections',
    [FeatureIdEnum.NEXT_QUERIES]: 'nextqueries',
    [FeatureIdEnum.BLACKLIST]: 'blacklist',
    [FeatureIdEnum.EQUALIZE]: 'equalize',
    [FeatureIdEnum.AUDIT]: 'audit',
    [FeatureIdEnum.INDEX]: 'index',
    [FeatureIdEnum.INSTANCE_MANAGEMENT]: 'config',
    [FeatureIdEnum.CLIENT_CONFIG]: 'config',
    [FeatureIdEnum.ENDPOINT_CONFIG]: 'config',
    [FeatureIdEnum.FEATURE_CONFIG]: 'config',
    [FeatureIdEnum.INDEX_CLIENT]: 'index',
    [FeatureIdEnum.INDEX_FEED_CONFIGURATION]: 'index',
    [FeatureIdEnum.INDEX_SCHEMA_CONFIGURATION]: 'index',
    [FeatureIdEnum.USER_ROLE_MANAGEMENT]: 'usergroup',
    [FeatureIdEnum.ACCOUNT]: 'usercircular',
    [FeatureIdEnum.BROWSE]: 'navigationdomain',
    [FeatureIdEnum.FACETS]: 'facets',
  }

  return icons[featureId]
}

function getTaggingConfig(featureId: FeatureIdEnum, domain: DomainEnum): TaggingConfig {
  return {
    id: getTaggingId(featureId, domain),
    category: getTaggingCategory(featureId),
    context: getTaggingContext(domain),
  }
}

export function getTaggingContext(domain: DomainEnum): NavigationModeEnum {
  switch (domain) {
    case DomainEnum.SEARCH:
      return NavigationModeEnum.SEARCH
    case DomainEnum.BROWSE:
      return NavigationModeEnum.NAVIGATION
    default:
      return NavigationModeEnum.GLOBAL
  }
}

function getTaggingCategory(featureId: FeatureIdEnum): EngagementCategoryEnum {
  return {
    [FeatureIdEnum.OVERALL]: EngagementCategoryEnum.ANALYTICS,
    [FeatureIdEnum.QUERIES]: EngagementCategoryEnum.ANALYTICS,
    [FeatureIdEnum.CATEGORIES]: EngagementCategoryEnum.ANALYTICS,
    [FeatureIdEnum.NOW]: EngagementCategoryEnum.INSIGHTS,
    [FeatureIdEnum.ORIGINS]: EngagementCategoryEnum.INSIGHTS,
    [FeatureIdEnum.KEYWORDS]: EngagementCategoryEnum.INSIGHTS,
    [FeatureIdEnum.EQUALIZE]: EngagementCategoryEnum.PLAY,
    [FeatureIdEnum.SYNONYM]: EngagementCategoryEnum.PLAY,
    [FeatureIdEnum.BLACKLIST]: EngagementCategoryEnum.PLAY,
    [FeatureIdEnum.PROMOTION]: EngagementCategoryEnum.PLAY,
    [FeatureIdEnum.REDIRECTION]: EngagementCategoryEnum.PLAY,
    [FeatureIdEnum.CONFIGURATION]: EngagementCategoryEnum.PLAY,
    [FeatureIdEnum.RELATED_TAGS]: EngagementCategoryEnum.PLAY,
    [FeatureIdEnum.NEXT_QUERIES]: EngagementCategoryEnum.PLAY,
    [FeatureIdEnum.EXPLAIN]: EngagementCategoryEnum.EXPLAIN,
    [FeatureIdEnum.AUDIT]: EngagementCategoryEnum.AUDIT,
    [FeatureIdEnum.FEEDBACK]: EngagementCategoryEnum.MODAL,
  }[featureId]
}

function getTaggingId(
  featureId: FeatureIdEnum,
  domain: DomainEnum,
): EngagementCategoryEnum | EngagementToolEnum {
  return {
    [FeatureIdEnum.INSIGHTS]: EngagementCategoryEnum.INSIGHTS,
    [FeatureIdEnum.PLAY]: EngagementCategoryEnum.PLAY,
    [FeatureIdEnum.ROOT_QUERY]: EngagementCategoryEnum.PLAY,
    [FeatureIdEnum.OVERALL]: EngagementToolEnum.OVERALL,
    [FeatureIdEnum.QUERIES]: EngagementToolEnum.QUERIES,
    [FeatureIdEnum.CATEGORIES]: EngagementToolEnum.CATEGORIES,
    [FeatureIdEnum.NOW]: EngagementToolEnum.NOW,
    [FeatureIdEnum.ORIGINS]: EngagementToolEnum.ORIGINS,
    [FeatureIdEnum.KEYWORDS]: EngagementToolEnum.KEYWORDS,
    [FeatureIdEnum.EQUALIZE]: EngagementToolEnum.EQUALIZE,
    [FeatureIdEnum.SYNONYM]: EngagementToolEnum.SYNONYMS,
    [FeatureIdEnum.BLACKLIST]: EngagementToolEnum.BLACKLIST,
    [FeatureIdEnum.PROMOTION]: EngagementToolEnum.PROMOTIONS,
    [FeatureIdEnum.REDIRECTION]: EngagementToolEnum.REDIRECTIONS,
    [FeatureIdEnum.CONFIGURATION]:
      domain === DomainEnum.SEARCH
        ? EngagementToolEnum.SEARCH_CONFIGURATIONS
        : EngagementToolEnum.NAVIGATION_CONFIGURATIONS,
    [FeatureIdEnum.RELATED_TAGS]: EngagementToolEnum.RELATED_TAGS,
    [FeatureIdEnum.NEXT_QUERIES]: EngagementToolEnum.NEXT_QUERIES,
    [FeatureIdEnum.EXPLAIN]: EngagementToolEnum.EXPLAIN,
    [FeatureIdEnum.AUDIT]: EngagementToolEnum.AUDIT,
    [FeatureIdEnum.FEEDBACK]: EngagementToolEnum.FEEDBACK,
  }[featureId]
}
